import {
  ALARM_CATEGORIES,
  ALARM_RULE_CODES,
  ALARM_RULES,
  ALARM_SEVERITIES,
  ALARM_SEVERITY_LABELS,
  ALARM_STATUS_LABELS,
  ALARM_STATUSES,
  type AlarmCategory,
  type AlarmRuleCode,
  type AlarmRuleView,
  type AlarmSeverity,
  type AlarmStatus,
  type AlarmSummary,
  type AlarmSummaryItem,
  type AlarmThresholdLimit,
  type AlarmTimelineEntry,
  digestContentSchema,
  type DigestContentView,
  type NotificationChannel,
  type NotificationChannelDefault,
  type NotificationDeliveryStatus,
  type NotificationItem,
  type NotificationPreferenceRowView,
  type SaveAlarmRuleOverride,
  type SaveNotificationPreferenceRow,
  type SeverityCounts,
} from "@station/contracts";
import { Money } from "@station/core";
import type { Tone } from "@station/ui";
import type { TFunction } from "i18next";

/**
 * Alarm ve bildirim ekranlarının SAF yardımcıları (Prompt 20 web): süzgeç ↔ URL, zaman çizelgesi metni, bağlantı çözümü,
 * kural geçersiz kılma taslağı ↔ istek gövdesi, tercih matrisi ↔ satır kümesi, özet (digest) verisinin normalleştirilmesi.
 * Bileşen içermez; `alarm-values.test.ts` birim testleriyle kapsanır. Türkçe metinler `tr.ts`'tedir (çeviri işlevi `t`
 * parametreyle gelir); durum / önem / kategori adları sözleşmedeki etiketlerdir.
 */

// ── Rozet renkleri (rozet her zaman METİN yazar; anlam yalnızca renkle taşınmaz) ────────
export const SEVERITY_TONE: Record<AlarmSeverity, Tone> = {
  CRITICAL: "danger",
  IMPORTANT: "warning",
  INFO: "info",
};

export const STATUS_TONE: Record<AlarmStatus, Tone> = {
  OPEN: "warning",
  ACKNOWLEDGED: "info",
  ASSIGNED: "info",
  RESOLVED: "success",
};

export const DELIVERY_STATUS_TONE: Record<NotificationDeliveryStatus, Tone> = {
  PENDING: "info",
  SENT: "success",
  FAILED: "danger",
  SKIPPED: "neutral",
};

/** Önem sırası: bilgi < önemli < kritik. */
export const SEVERITY_RANK: Record<AlarmSeverity, number> = { INFO: 0, IMPORTANT: 1, CRITICAL: 2 };

export function maxSeverity(severities: readonly AlarmSeverity[]): AlarmSeverity {
  return severities.reduce<AlarmSeverity>(
    (best, item) => (SEVERITY_RANK[item] > SEVERITY_RANK[best] ? item : best),
    "INFO",
  );
}

// ── Liste süzgeçleri ↔ URL ───────────────────────────────────────────────────
/**
 * URL'deki arama parametreleri. Motorun grup bildirim bağlantısı `/alarmlar?stationId=…&category=…` biçimindedir; bu
 * yüzden adlar API'ninkilerle aynıdır. Çoklu seçim virgüllü dizgedir (`status=OPEN,ASSIGNED`); "yalnızca açıklar"
 * VARSAYILAN olarak açıktır — yalnızca kapatıldığında URL'ye `openOnly=false` yazılır.
 */
export interface AlarmSearch {
  status?: string | undefined;
  severity?: string | undefined;
  stationId?: string | undefined;
  category?: AlarmCategory | undefined;
  ruleCode?: AlarmRuleCode | undefined;
  openOnly?: boolean | undefined;
  assignedToMe?: boolean | undefined;
}

export interface AlarmFilters {
  status: AlarmStatus[];
  severity: AlarmSeverity[];
  stationId: string | null;
  category: AlarmCategory | null;
  ruleCode: AlarmRuleCode | null;
  openOnly: boolean;
  assignedToMe: boolean;
}

export const DEFAULT_ALARM_FILTERS: AlarmFilters = {
  status: [],
  severity: [],
  stationId: null,
  category: null,
  ruleCode: null,
  openOnly: true,
  assignedToMe: false,
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function listOf<T extends string>(raw: unknown, allowed: readonly T[]): T[] {
  const items = Array.isArray(raw) ? raw.map(String) : typeof raw === "string" ? raw.split(",") : [];
  const picked = items.map((item) => item.trim()).filter((item): item is T => allowed.includes(item as T));
  // Tekil ve sözleşmedeki sırayla (URL'nin biçimi yazılış sırasından bağımsızdır).
  return allowed.filter((value) => picked.includes(value));
}

function flag(raw: unknown): boolean | undefined {
  if (raw === true || raw === "true") return true;
  if (raw === false || raw === "false") return false;
  return undefined;
}

/**
 * Yönlendiricinin `validateSearch`'ü: bozuk / bilinmeyen değerler sessizce düşer (paylaşılan bağlantı sayfayı kırmaz).
 * Yönlendirici değerleri JSON olarak çözer (`openOnly=false` → `false`), elle yazılmış dizgeler de kabul edilir.
 */
export function parseAlarmSearch(raw: Record<string, unknown>): AlarmSearch {
  const status = listOf(raw.status, ALARM_STATUSES);
  const severity = listOf(raw.severity, ALARM_SEVERITIES);
  const category = ALARM_CATEGORIES.find((value) => value === raw.category);
  const ruleCode = ALARM_RULE_CODES.find((value) => value === raw.ruleCode);
  const openOnly = flag(raw.openOnly);
  const assignedToMe = flag(raw.assignedToMe);
  return compactSearch({
    status: status.length > 0 ? status.join(",") : undefined,
    severity: severity.length > 0 ? severity.join(",") : undefined,
    stationId: typeof raw.stationId === "string" && UUID.test(raw.stationId) ? raw.stationId : undefined,
    category,
    ruleCode,
    openOnly: openOnly === false ? false : undefined,
    assignedToMe: assignedToMe === true ? true : undefined,
  });
}

function compactSearch(search: AlarmSearch): AlarmSearch {
  return Object.fromEntries(
    Object.entries(search).filter(([, value]) => value !== undefined && value !== "" && value !== null),
  ) as AlarmSearch;
}

export function filtersFromSearch(search: AlarmSearch): AlarmFilters {
  return {
    status: listOf(search.status, ALARM_STATUSES),
    severity: listOf(search.severity, ALARM_SEVERITIES),
    stationId: search.stationId ?? null,
    category: search.category ?? null,
    ruleCode: search.ruleCode ?? null,
    openOnly: search.openOnly !== false,
    assignedToMe: search.assignedToMe === true,
  };
}

export function searchFromFilters(filters: AlarmFilters): AlarmSearch {
  return compactSearch({
    status: filters.status.length > 0 ? listOf(filters.status, ALARM_STATUSES).join(",") : undefined,
    severity: filters.severity.length > 0 ? listOf(filters.severity, ALARM_SEVERITIES).join(",") : undefined,
    stationId: filters.stationId ?? undefined,
    category: filters.category ?? undefined,
    ruleCode: filters.ruleCode ?? undefined,
    openOnly: filters.openOnly ? undefined : false,
    assignedToMe: filters.assignedToMe ? true : undefined,
  });
}

/** API sorgusu (`GET /alarms`): virgüllü listeler, `"true"/"false"` bayrakları; boş olanlar gönderilmez. */
export function alarmListQuery(
  filters: AlarmFilters,
  cursor?: string,
  limit = 25,
): Record<string, string | number> {
  const query: Record<string, string | number> = { limit, openOnly: filters.openOnly ? "true" : "false" };
  if (filters.status.length > 0) query.status = listOf(filters.status, ALARM_STATUSES).join(",");
  if (filters.severity.length > 0) query.severity = listOf(filters.severity, ALARM_SEVERITIES).join(",");
  if (filters.stationId) query.stationId = filters.stationId;
  if (filters.category) query.category = filters.category;
  if (filters.ruleCode) query.ruleCode = filters.ruleCode;
  if (filters.assignedToMe) query.assignedToMe = "true";
  if (cursor) query.cursor = cursor;
  return query;
}

/**
 * Durum kutusunu değiştir. "Çözüldü" seçilince "yalnızca açıklar" kapanır (ikisi birlikte boş liste verir); tersine
 * "yalnızca açıklar" açılınca `setOpenOnly` çözülmüş durumu listeden çıkarır.
 */
export function toggleStatus(filters: AlarmFilters, status: AlarmStatus, checked: boolean): AlarmFilters {
  const next = checked
    ? listOf([...filters.status, status], ALARM_STATUSES)
    : filters.status.filter((item) => item !== status);
  return { ...filters, status: next, openOnly: checked && status === "RESOLVED" ? false : filters.openOnly };
}

export function toggleSeverity(
  filters: AlarmFilters,
  severity: AlarmSeverity,
  checked: boolean,
): AlarmFilters {
  const next = checked
    ? listOf([...filters.severity, severity], ALARM_SEVERITIES)
    : filters.severity.filter((item) => item !== severity);
  return { ...filters, severity: next };
}

export function setOpenOnly(filters: AlarmFilters, openOnly: boolean): AlarmFilters {
  return {
    ...filters,
    openOnly,
    status: openOnly ? filters.status.filter((item) => item !== "RESOLVED") : filters.status,
  };
}

/** Kategori değişince o kategoriye ait olmayan kural seçimi düşer. */
export function setCategory(filters: AlarmFilters, category: AlarmCategory | null): AlarmFilters {
  const keepRule =
    filters.ruleCode !== null && (category === null || ALARM_RULES[filters.ruleCode].category === category);
  return { ...filters, category, ruleCode: keepRule ? filters.ruleCode : null };
}

export function isDefaultFilters(filters: AlarmFilters): boolean {
  return JSON.stringify(searchFromFilters(filters)) === "{}";
}

// ── Bağlantılar ─────────────────────────────────────────────────────────────
export interface AppLink {
  to: string;
  search: Record<string, string>;
}

/**
 * API'nin döndürdüğü uygulama içi yol (`/alarmlar/{id}`, `/akaryakit/mutabakat?station=…`) → yönlendirici hedefi.
 * Yalnızca aynı kökenli, `/` ile başlayan yollar kabul edilir (`//evil.example`, `https://…` açık yönlendirme olamaz).
 */
export function parseAppLink(link: string | null | undefined): AppLink | null {
  if (!link || !link.startsWith("/") || link.startsWith("//") || link.includes("\\")) return null;
  let url: URL;
  try {
    url = new URL(link, "https://uygulama.invalid");
  } catch {
    return null;
  }
  if (url.origin !== "https://uygulama.invalid") return null;
  return { to: url.pathname, search: Object.fromEntries(url.searchParams.entries()) };
}

/**
 * Alarmın "ilgili ekran" bağlantısı. Motor bağlantıyı hedef ekranın okuduğu parametrelerle ve alarmın şirketiyle
 * (`company=`) üretir; burada yalnızca ESKİ alarmlar için (bağlantıda istasyon var, şirket yok) alarmın şirketi eklenir —
 * istasyon bazlı ekranlar (akaryakıt, vardiya, market) istasyonu ŞİRKETİN içinde arar, seçili şirket başkaysa istasyon
 * bulunamazdı. Bağlantıdaki şirket her zaman önceliklidir.
 */
export function relatedScreenLink(link: string | null | undefined, companyId: string | null): AppLink | null {
  const parsed = parseAppLink(link);
  if (!parsed) return null;
  if (parsed.search.station && !parsed.search.company && companyId)
    return { ...parsed, search: { company: companyId, ...parsed.search } };
  return parsed;
}

// ── Zaman çizelgesi ─────────────────────────────────────────────────────────
const AUTO_RESOLVE_REASONS = [
  "LEVEL_RECOVERED",
  "SOURCE_EVENT",
  "CHECK",
  "DAY_ENDED",
  "SALES_RECOVERED",
  "NO_SHORTFALL",
] as const;

const numberOf = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const severityOf = (value: unknown): AlarmSeverity | null =>
  ALARM_SEVERITIES.find((item) => item === value) ?? null;

/** Zaman çizelgesi satırının açıklaması (tür etiketinin yanında): durum geçişi, önem, atanan, yükseltme, çözülme nedeni. */
export function timelineDetail(entry: AlarmTimelineEntry, t: TFunction): string[] {
  const parts: string[] = [];
  const data = entry.data;
  if (entry.fromStatus && entry.toStatus && entry.fromStatus !== entry.toStatus)
    parts.push(
      t("alarms.timeline.transition", {
        from: ALARM_STATUS_LABELS[entry.fromStatus],
        to: ALARM_STATUS_LABELS[entry.toStatus],
      }),
    );
  switch (entry.kind) {
    case "RAISED":
      parts.push(data.source === "CHECK" ? t("alarms.timeline.fromCheck") : t("alarms.timeline.fromEvent"));
      if (entry.severity) parts.push(ALARM_SEVERITY_LABELS[entry.severity]);
      break;
    case "REPEATED": {
      const count = numberOf(data.occurrenceCount);
      if (count !== null) parts.push(t("alarms.timeline.occurrence", { count }));
      break;
    }
    case "SEVERITY_RAISED": {
      const previous = severityOf(data.previousSeverity);
      if (previous && entry.severity)
        parts.push(
          t("alarms.timeline.transition", {
            from: ALARM_SEVERITY_LABELS[previous],
            to: ALARM_SEVERITY_LABELS[entry.severity],
          }),
        );
      break;
    }
    case "ASSIGNED":
      if (entry.assignee) parts.push(t("alarms.timeline.assignee", { name: entry.assignee.fullName }));
      break;
    case "ESCALATED": {
      const from = numberOf(data.fromLevel);
      const to = numberOf(data.toLevel);
      const recipients = numberOf(data.recipientCount);
      if (from !== null && to !== null) parts.push(t("alarms.timeline.escalationLevel", { from, to }));
      if (data.reason === "NO_NEW_RECIPIENTS") parts.push(t("alarms.timeline.noNewRecipients"));
      else if (recipients !== null) parts.push(t("alarms.timeline.notifiedUsers", { count: recipients }));
      if (data.fallback === "OWNERS") parts.push(t("alarms.timeline.fallbackOwners"));
      break;
    }
    case "NOTIFIED": {
      const users = numberOf(data.userCount);
      if (users !== null) parts.push(t("alarms.timeline.notifiedUsers", { count: users }));
      break;
    }
    case "AUTO_RESOLVED": {
      const reason = AUTO_RESOLVE_REASONS.find((item) => item === data.reason);
      parts.push(reason ? t(`alarms.timeline.reason.${reason}`) : t("alarms.timeline.reasonUnknown"));
      break;
    }
    default:
      break;
  }
  return parts;
}

// ── Kapsam ve özet ──────────────────────────────────────────────────────────
/** Liste / ayrıntı satırının kapsamı: istasyon ("IST01 · Kadıköy"), şirket geneli ya da kiracı geneli. */
export function alarmScopeText(
  alarm: { stationCode: string | null; stationName: string | null; companyName: string | null },
  t: TFunction,
): string {
  if (alarm.stationCode)
    return alarm.stationName ? `${alarm.stationCode} · ${alarm.stationName}` : alarm.stationCode;
  if (alarm.companyName) return t("alarms.scope.company", { name: alarm.companyName });
  return t("alarms.scope.tenant");
}

const EMPTY_COUNTS: SeverityCounts = { CRITICAL: 0, IMPORTANT: 0, INFO: 0, total: 0 };

/**
 * Ana sayfa kartı: tek istasyon görünümünde (tek istasyonlu kiracı ya da üst çubukta tek istasyon seçili) sayılar ve en
 * yeniler o istasyona indirgenir; genel bakışta kiracının görünür tüm açık alarmlarıdır.
 */
export function summaryForStation(
  summary: AlarmSummary,
  stationId: string | null,
): { counts: SeverityCounts; latest: AlarmSummaryItem[]; assignedToMe: number } {
  if (stationId === null)
    return { counts: summary.open, latest: summary.latest, assignedToMe: summary.assignedToMe };
  const station = summary.byStation.find((item) => item.stationId === stationId);
  return {
    counts: station
      ? { CRITICAL: station.CRITICAL, IMPORTANT: station.IMPORTANT, INFO: station.INFO, total: station.total }
      : EMPTY_COUNTS,
    latest: summary.latest.filter((item) => item.stationId === stationId),
    assignedToMe: summary.assignedToMe,
  };
}

// ── Kural ayarları ──────────────────────────────────────────────────────────
export type RuleLevel = "TENANT" | "STATION";
export type OverrideSource = "CATALOG" | "TENANT" | "STATION";

/** Etkin değerleri hangi düzey belirliyor: istasyon satırı > kiracı satırı > katalog. */
export function overrideSource(rule: AlarmRuleView): OverrideSource {
  if (rule.stationOverride) return "STATION";
  if (rule.tenantOverride) return "TENANT";
  return "CATALOG";
}

/** Etkin önem metni: zorlanan önem ya da sinyale göre ("Önemli / Kritik"). */
export function effectiveSeverityText(rule: AlarmRuleView, t: TFunction): string {
  if (rule.effective.forcedSeverity) return ALARM_SEVERITY_LABELS[rule.effective.forcedSeverity];
  if (rule.defaults.severities.length > 1)
    return t("alarms.rules.bySignal", {
      severities: rule.defaults.severities.map((item) => ALARM_SEVERITY_LABELS[item]).join(" / "),
    });
  return ALARM_SEVERITY_LABELS[rule.defaults.severity];
}

/** Eşik değeri Türkçe biçimde: `minBaselineAmount` kuruştur (TL gösterilir), diğerleri yüzde / gün / hafta / saat. */
export function formatThreshold(key: string, value: number): string {
  if (key === "minBaselineAmount") return Money.fromMinor(String(Math.round(value))).formatTr();
  return formatLimit(value);
}

/** Eşik etiketi: sözleşmedeki etiket (para eşiğinde "(₺)" — değer kuruş saklanır, arayüz ₺ gösterir ve ₺ girer). */
export function thresholdLabel(key: string, label: string | undefined): string {
  return label ?? key;
}

export interface RuleDraft {
  /** "" = üst düzeyden. */
  enabled: "" | "true" | "false";
  severity: "" | AlarmSeverity;
  /** Eşik girişi (Türkçe ondalık, `minBaselineAmount` KURUŞ dizgesi); "" = üst düzeyden. */
  thresholds: Record<string, string>;
  escalationMinutes: string;
}

function thresholdInput(key: string, value: number): string {
  if (key === "minBaselineAmount") return String(Math.round(value));
  return String(value).replace(".", ",");
}

/** Düzenlenen düzeyin geçersiz kılma satırından taslak (satır yoksa her alan "üst düzeyden"). */
export function ruleDraftFrom(rule: AlarmRuleView, level: RuleLevel): RuleDraft {
  const override = level === "STATION" ? rule.stationOverride : rule.tenantOverride;
  return {
    enabled:
      override?.enabled === null || override?.enabled === undefined
        ? ""
        : override.enabled
          ? "true"
          : "false",
    severity: override?.severity ?? "",
    thresholds: Object.fromEntries(
      Object.keys(rule.defaults.thresholds).map((key) => {
        const value = override?.thresholds?.[key];
        return [key, value === undefined ? "" : thresholdInput(key, value)];
      }),
    ),
    escalationMinutes:
      override?.escalationMinutes === null || override?.escalationMinutes === undefined
        ? ""
        : String(override.escalationMinutes),
  };
}

/** Düzenlenen düzeyin "üst düzeyi": istasyonda kiracı satırı ya da katalog, kiracıda katalog. */
export function upstreamValues(
  rule: AlarmRuleView,
  level: RuleLevel,
): {
  enabled: boolean;
  severity: AlarmSeverity | null;
  thresholds: Record<string, number>;
  escalationMinutes: number | null;
} {
  const tenant = level === "STATION" ? rule.tenantOverride : null;
  return {
    enabled: tenant?.enabled ?? true,
    severity: tenant?.severity ?? null,
    thresholds: { ...rule.defaults.thresholds, ...(tenant?.thresholds ?? {}) },
    escalationMinutes: tenant?.escalationMinutes ?? rule.defaults.escalationMinutes,
  };
}

/** "2,5" / "2.5" / "2" → sayı; boş → `null`; geçersiz → `NaN`. */
export function parseDecimalInput(text: string): number | null {
  const trimmed = text.trim().replace(/\s/g, "");
  if (trimmed === "") return null;
  if (!/^-?\d+([.,]\d+)?$/.test(trimmed)) return Number.NaN;
  return Number(trimmed.replace(",", "."));
}

/** Eşik girişini sınırlarla doğrular; sorun yoksa `null`. */
export function thresholdIssue(
  value: number | null,
  limit: AlarmThresholdLimit,
  t: TFunction,
): string | null {
  if (value === null) return null;
  if (Number.isNaN(value)) return t("alarms.rules.invalidNumber");
  if (limit.integer && !Number.isInteger(value)) return t("alarms.rules.integerRequired");
  if (!limit.integer && Math.round(value * 100) !== value * 100) return t("alarms.rules.twoDecimals");
  if (value < limit.min || value > limit.max)
    return t("alarms.rules.outOfRange", { min: formatLimit(limit.min), max: formatLimit(limit.max) });
  return null;
}

function formatLimit(value: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(value);
}

/** Taslak → `PUT /alarm-rules/{code}` gövdesi; hatalar alan anahtarıyla (`thresholds.dropPercent`, `escalationMinutes`). */
export function ruleOverrideBody(
  rule: AlarmRuleView,
  draft: RuleDraft,
  stationId: string | null,
  t: TFunction,
): { body: SaveAlarmRuleOverride; issues: Record<string, string> } {
  const issues: Record<string, string> = {};
  const thresholds: Record<string, number> = {};
  for (const [key, text] of Object.entries(draft.thresholds)) {
    const limit = rule.thresholdLimits[key];
    const value =
      key === "minBaselineAmount" && /^\d+$/.test(text.trim())
        ? Number(text.trim())
        : parseDecimalInput(text);
    const issue = limit ? thresholdIssue(value, limit, t) : null;
    if (issue) issues[`thresholds.${key}`] = issue;
    else if (value !== null && !Number.isNaN(value)) thresholds[key] = value;
  }
  const merged = { ...upstreamValues(rule, stationId ? "STATION" : "TENANT").thresholds, ...thresholds };
  if (
    merged.weeks !== undefined &&
    merged.minWeeks !== undefined &&
    merged.minWeeks > merged.weeks &&
    !issues["thresholds.minWeeks"]
  )
    issues["thresholds.minWeeks"] = t("alarms.rules.minWeeksAboveWeeks");
  let escalationMinutes: number | null = null;
  if (rule.defaults.escalationMinutes !== null && draft.escalationMinutes.trim() !== "") {
    const value = parseDecimalInput(draft.escalationMinutes);
    if (value === null || Number.isNaN(value) || !Number.isInteger(value) || value < 1 || value > 1440)
      issues.escalationMinutes = t("alarms.rules.escalationRange");
    else escalationMinutes = value;
  }
  const override = stationId ? rule.stationOverride : rule.tenantOverride;
  return {
    body: {
      stationId,
      enabled: draft.enabled === "" ? null : draft.enabled === "true",
      severity: draft.severity === "" ? null : draft.severity,
      thresholds: Object.keys(thresholds).length > 0 ? thresholds : null,
      escalationMinutes,
      ...(override ? { version: override.version } : {}),
    },
    issues,
  };
}

// ── Bildirim tercih matrisi ─────────────────────────────────────────────────
/**
 * Matris hücresi: satır (tümü / kategori / kural) × kanal, seçili istasyon kapsamında (boş = tüm istasyonlar).
 * `DEFAULT` = bu hücre için satır YOK (daha genel satırdan ya da rolden gelir); `ON` = açık, kanalın varsayılan eşiğiyle.
 */
export type CellValue = "DEFAULT" | "OFF" | "ON" | `ON_${AlarmSeverity}`;

export interface CellTarget {
  stationId: string | null;
  category: AlarmCategory | null;
  ruleCode: AlarmRuleCode | null;
  channel: NotificationChannel;
}

export type PreferenceRow = Pick<
  NotificationPreferenceRowView,
  "stationId" | "category" | "ruleCode" | "channel" | "enabled" | "minSeverity"
>;

const sameCell = (row: PreferenceRow, target: CellTarget): boolean =>
  row.stationId === target.stationId &&
  row.category === target.category &&
  row.ruleCode === target.ruleCode &&
  row.channel === target.channel;

export function cellValue(rows: readonly PreferenceRow[], target: CellTarget): CellValue {
  const row = rows.find((item) => sameCell(item, target));
  if (!row) return "DEFAULT";
  if (!row.enabled) return "OFF";
  return row.minSeverity ? `ON_${row.minSeverity}` : "ON";
}

/** Hücreyi değiştirir: aynı hücrenin satırı düşer, `DEFAULT` dışındaki değerde yeni satır eklenir (sıra kararlı). */
export function setCellValue(
  rows: readonly PreferenceRow[],
  target: CellTarget,
  value: CellValue,
): PreferenceRow[] {
  const rest = rows.filter((item) => !sameCell(item, target));
  if (value === "DEFAULT") return rest;
  const minSeverity = value === "OFF" || value === "ON" ? null : (value.slice(3) as AlarmSeverity);
  return sortPreferenceRows([...rest, { ...target, enabled: value !== "OFF", minSeverity }]);
}

const channelOrder = (channel: NotificationChannel) => ["IN_APP", "EMAIL", "SMS", "PUSH"].indexOf(channel);

export function sortPreferenceRows(rows: readonly PreferenceRow[]): PreferenceRow[] {
  const key = (row: PreferenceRow) =>
    [
      row.stationId ? `1${row.stationId}` : "0",
      row.category ? `1${String(ALARM_CATEGORIES.indexOf(row.category)).padStart(2, "0")}` : "0",
      row.ruleCode ? `1${String(ALARM_RULE_CODES.indexOf(row.ruleCode)).padStart(2, "0")}` : "0",
      String(channelOrder(row.channel)),
    ].join("|");
  return [...rows].sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0));
}

function specificity(target: CellTarget): number {
  return (target.ruleCode ? 4 : target.category ? 2 : 0) + (target.stationId ? 1 : 0);
}

/** Satır hedefe uyuyor mu (API'nin `effectiveChannelPreference` kuralıyla aynı: kural > kategori > tümü, istasyon > tümü). */
function rowMatches(row: PreferenceRow, target: CellTarget): boolean {
  if (row.channel !== target.channel) return false;
  if (row.stationId !== null && row.stationId !== target.stationId) return false;
  const targetCategory = target.ruleCode ? ALARM_RULES[target.ruleCode].category : target.category;
  if (row.ruleCode !== null && row.ruleCode !== target.ruleCode) return false;
  if (row.category !== null && row.category !== targetCategory) return false;
  return true;
}

export interface InheritedPreference {
  enabled: boolean;
  minSeverity: AlarmSeverity;
  source: "ROW" | "ROLE_DEFAULT";
}

/**
 * Hücrenin KENDİ satırı yokken geçerli olan tercih: hedefe uyan, hücreden DAHA GENEL satırların en özgülü; hiçbiri yoksa
 * rol varsayılanı. Satırın eşiği boşsa kanalın asgari önemi.
 */
export function inheritedPreference(
  rows: readonly PreferenceRow[],
  target: CellTarget,
  roleDefaults: readonly NotificationChannelDefault[],
  channelMinSeverity: readonly Pick<NotificationChannelDefault, "channel" | "minSeverity">[],
): InheritedPreference {
  const own = specificity(target);
  let best: PreferenceRow | null = null;
  let bestScore = -1;
  for (const row of rows) {
    if (!rowMatches(row, target)) continue;
    const score = specificity(row);
    if (score < own && score > bestScore) {
      best = row;
      bestScore = score;
    }
  }
  const channelDefault =
    channelMinSeverity.find((item) => item.channel === target.channel)?.minSeverity ?? "INFO";
  if (best) return { enabled: best.enabled, minSeverity: best.minSeverity ?? channelDefault, source: "ROW" };
  const role = roleDefaults.find((item) => item.channel === target.channel);
  return {
    enabled: role?.enabled ?? false,
    minSeverity: role?.minSeverity ?? channelDefault,
    source: "ROLE_DEFAULT",
  };
}

/** Önem eşiği metni: "tümü", "önemli ve üstü", "yalnızca kritik". */
export function minSeverityText(severity: AlarmSeverity, t: TFunction): string {
  return t(`notifications.matrix.minSeverity.${severity}`);
}

/** "Varsayılan: açık (önemli ve üstü)" / "Varsayılan: kapalı". */
export function inheritedText(pref: InheritedPreference, t: TFunction): string {
  return pref.enabled
    ? t("notifications.matrix.defaultOn", { severity: minSeverityText(pref.minSeverity, t) })
    : t("notifications.matrix.defaultOff");
}

/**
 * Hücre seçenekleri. Kural satırında kuralın üretemeyeceği önemin üstündeki eşik anlamsızdır (ör. yalnızca "bilgi"
 * üreten kuralda "yalnızca kritik" hiçbir bildirimi geçirmez) → gösterilmez. `ON` (kanal varsayılanı) yalnızca kayıtlı
 * satır öyleyse gösterilir.
 */
export function cellOptions(current: CellValue, ruleCode: AlarmRuleCode | null): CellValue[] {
  const ceiling = ruleCode ? SEVERITY_RANK[maxSeverity(ALARM_RULES[ruleCode].severities)] : 2;
  // Kısıtlayıcılığı artan sırada: tümü → önemli ve üstü → yalnızca kritik.
  const severities = ALARM_SEVERITIES.filter((severity) => SEVERITY_RANK[severity] <= ceiling).reverse();
  const options: CellValue[] = ["DEFAULT", ...severities.map((severity) => `ON_${severity}` as CellValue)];
  if (current === "ON") options.push("ON");
  if (current.startsWith("ON_") && !options.includes(current)) options.push(current);
  options.push("OFF");
  return options;
}

export function toSavePreferenceRows(rows: readonly PreferenceRow[]): SaveNotificationPreferenceRow[] {
  return sortPreferenceRows(rows).map((row) => ({
    stationId: row.stationId,
    category: row.category,
    ruleCode: row.ruleCode,
    channel: row.channel,
    enabled: row.enabled,
    minSeverity: row.minSeverity,
  }));
}

export function preferencesEqual(a: readonly PreferenceRow[], b: readonly PreferenceRow[]): boolean {
  return JSON.stringify(toSavePreferenceRows(a)) === JSON.stringify(toSavePreferenceRows(b));
}

/** Kategori satırının altındaki kural satırlarından bu istasyon kapsamında özel ayarı olanların sayısı. */
export function customRuleCount(
  rows: readonly PreferenceRow[],
  stationId: string | null,
  category: AlarmCategory,
): number {
  return new Set(
    rows
      .filter(
        (row) =>
          row.stationId === stationId &&
          row.ruleCode !== null &&
          ALARM_RULES[row.ruleCode].category === category,
      )
      .map((row) => row.ruleCode),
  ).size;
}

// ── Bildirim kutusu ─────────────────────────────────────────────────────────
/**
 * "Tümünü okundu say" sınırı: görünen en yeni bildirimin anı + 1 ms (sunucu `created_at <= before` karşılaştırır; ISO
 * dizgesi milisaniyeye yuvarlanır). Liste boşsa sınır yok (tümü).
 */
export function markAllBefore(items: readonly Pick<NotificationItem, "createdAt">[]): string | undefined {
  const newest = items.reduce<number | null>((best, item) => {
    const at = Date.parse(item.createdAt);
    return Number.isNaN(at) ? best : best === null || at > best ? at : best;
  }, null);
  return newest === null ? undefined : new Date(newest + 1).toISOString();
}

/**
 * Yüzde değişim ("-12.5" → "−%12,5", "3.7" → "+%3,7"); karşılaştırılan dönem 0 ise "—". Motorun özet gövdesiyle AYNI
 * biçimleyici (sözleşmeden).
 */
export { formatPercentChange } from "@station/contracts";

// ── Özet (digest) ───────────────────────────────────────────────────────────
/** Özetin yapısal içeriği: bildirimin `data.digest`'i ve önizleme (içerik + başlık / gövde / gönderim) aynı şemadır. */
export type DigestContent = DigestContentView;

/**
 * Özet bildiriminin yapısal içeriği (`data.digest`) — sözleşmenin `digestContentSchema`'sıyla doğrulanır (API zaten
 * ayrıştırır; bu ikinci savunma hattıdır). Türü bildirim türüyle uyuşmayan, eski / bozuk biçimli veri `null` döner (bildirim
 * düz metin gövdesiyle gösterilir, ekran çökmez).
 */
export function digestFromNotification(
  notification: Pick<NotificationItem, "kind" | "data">,
): DigestContent | null {
  const expected =
    notification.kind === "DIGEST_DAILY" ? "DAILY" : notification.kind === "DIGEST_WEEKLY" ? "WEEKLY" : null;
  if (expected === null) return null;
  const data: unknown = notification.data;
  const raw = typeof data === "object" && data !== null ? (data as { digest?: unknown }).digest : undefined;
  const parsed = digestContentSchema.safeParse(raw);
  return parsed.success && parsed.data.kind === expected ? parsed.data : null;
}
